import React from 'react';
import {Pressable, Text, TouchableOpacity, View} from 'react-native';
import i18n from 'i18n-js';
import {fontFamily} from '../../../helpers/fontFamily';
import {
  accentColor,
  dangerColor,
  groupBg,
  groupBorderColor,
  groupCountPillBg,
  groupCountPillText,
  groupDimmedOpacity,
  groupRadius,
  matchRowBg,
  matchRowBorderColor,
  textColor,
  textInputBg,
  textMutedColor,
  touchTargetMin
} from '../../../style/adminTools';
import {dangerTextOnDark} from '../../../style/adminTenantGrid';
import {
  formatActivityTime,
  hasValue,
  isOverPlanMax,
  latestActivity
} from './TenantCells';
import {currentPeriodText, formatPlan, isUnlimited} from './tenantGridFormat';

const t = (key, options) => i18n.t(`adminTenantGrid.${key}`, options);

const CHECKBOX_SIZE = 22;

const formatNumber = value => {
  const number = Number(value);
  return Number.isNaN(number) ? String(value) : number.toLocaleString('en-US');
};

const valueOrDash = value =>
  hasValue(value) ? formatNumber(value) : t('dash');

const StatusPill = ({data}) => {
  const ready = !data.is_deleted;
  const label = ready ? 'statusActive' : 'statusDeleted';
  return (
    <View
      testID="phoneCardStatus"
      style={{
        backgroundColor: ready ? accentColor : groupCountPillBg,
        borderRadius: 12,
        paddingHorizontal: 10,
        paddingVertical: 3
      }}
    >
      <Text style={groupCountPillText}>{t(label)}</Text>
    </View>
  );
};

const UserTile = ({label, value, testID}) => (
  <View
    style={{
      flex: 1,
      padding: 10,
      borderRadius: 6,
      backgroundColor: textInputBg
    }}
  >
    <Text
      style={{
        fontFamily: fontFamily.font500,
        fontSize: 11,
        color: textMutedColor
      }}
    >
      {label}
    </Text>
    <Text
      testID={testID}
      style={{fontFamily: fontFamily.font700, fontSize: 18, color: textColor}}
    >
      {valueOrDash(value)}
    </Text>
  </View>
);

const ShippedBar = ({data}) => {
  const shipped = Number(data.total_shipped);
  const max = Number(data.max_allowed);
  const unlimited = isUnlimited(data.max_allowed);
  const known = hasValue(data.total_shipped) && hasValue(data.max_allowed);
  const over = isOverPlanMax(data);
  const fill = known && max > 0 ? Math.min(100, (shipped / max) * 100) : 0;
  const period = currentPeriodText(data);
  return (
    <View style={{marginTop: 10}}>
      <View style={{flexDirection: 'row', justifyContent: 'space-between'}}>
        <Text
          style={{
            fontFamily: fontFamily.font500,
            fontSize: 12,
            color: textMutedColor
          }}
        >
          {period
            ? `${t('colShippedThisMonth')} · ${period}`
            : t('colShippedThisMonth')}
        </Text>
        <Text
          testID={`phoneShipped-${data.id}`}
          style={{
            fontFamily: fontFamily.font600,
            fontSize: 12,
            color: over ? dangerTextOnDark : textColor
          }}
        >
          {t('shippedOfMax', {
            shipped: valueOrDash(data.total_shipped),
            max: unlimited ? t('unlimited') : valueOrDash(data.max_allowed)
          })}
        </Text>
      </View>
      {unlimited ? null : (
        <View
          style={{
            height: 6,
            marginTop: 6,
            borderRadius: 3,
            backgroundColor: textInputBg
          }}
        >
          <View
            testID={`phoneShippedBar-${data.id}`}
            style={{
              width: `${fill}%`,
              height: 6,
              borderRadius: 3,
              backgroundColor: over ? dangerColor : accentColor
            }}
          />
        </View>
      )}
    </View>
  );
};

const Checkbox = ({checked, id, name, onToggle}) => (
  <Pressable
    testID={`phoneCardCheck-${id}`}
    accessibilityRole="checkbox"
    accessibilityLabel={name}
    accessibilityState={{checked}}
    onPress={onToggle}
    style={{
      width: touchTargetMin,
      height: touchTargetMin,
      justifyContent: 'center',
      alignItems: 'center'
    }}
  >
    <View
      style={{
        width: CHECKBOX_SIZE,
        height: CHECKBOX_SIZE,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 4,
        borderWidth: 2,
        borderColor: checked ? accentColor : textMutedColor,
        backgroundColor: checked ? accentColor : 'transparent'
      }}
    >
      {checked ? (
        <Text style={{color: textColor, fontSize: 14, lineHeight: 16}}>
          {'✓'}
        </Text>
      ) : null}
    </View>
  </Pressable>
);

const TenantPhoneCard = ({
  data,
  selectMode,
  selected,
  onToggle,
  onOpenTenant
}) => {
  const latest = latestActivity(data.last_activity);
  const name = (
    <Text
      numberOfLines={1}
      style={{
        flex: 1,
        fontFamily: fontFamily.font600,
        fontSize: 16,
        color: accentColor
      }}
    >
      {data.name}
    </Text>
  );

  return (
    <View
      testID={`phoneCard-${data.id}`}
      style={{
        marginBottom: 10,
        padding: 14,
        backgroundColor: selected ? matchRowBg : groupBg,
        borderWidth: 1,
        borderColor: selected ? matchRowBorderColor : groupBorderColor,
        borderRadius: groupRadius,
        opacity: data.is_deleted ? groupDimmedOpacity : 1
      }}
    >
      <View style={{flexDirection: 'row', alignItems: 'center'}}>
        {selectMode ? (
          <Checkbox
            id={data.id}
            name={data.name}
            checked={selected}
            onToggle={onToggle}
          />
        ) : null}
        {selectMode ? (
          <Pressable
            testID={`phoneCardName-${data.id}`}
            onPress={onToggle}
            style={{
              flex: 1,
              minHeight: touchTargetMin,
              justifyContent: 'center'
            }}
          >
            {name}
          </Pressable>
        ) : (
          <TouchableOpacity
            testID={`tenantLink-${data.id}`}
            accessibilityRole="link"
            onPress={() => onOpenTenant(data)}
            style={{
              flex: 1,
              minHeight: touchTargetMin,
              justifyContent: 'center'
            }}
          >
            {name}
          </TouchableOpacity>
        )}
        <StatusPill data={data} />
      </View>
      <Text
        testID={`phoneCardPlan-${data.id}`}
        style={{
          marginTop: 4,
          fontFamily: fontFamily.font500,
          fontSize: 14,
          color: textColor
        }}
      >
        {formatPlan(data) || t('dash')}
      </Text>
      <View style={{flexDirection: 'row', marginTop: 10}}>
        <UserTile
          testID={`phoneRegularUsers-${data.id}`}
          label={t('sortRegularUsers')}
          value={data.regular_users_count}
        />
        <View style={{width: 10}} />
        <UserTile
          testID={`phoneAdminOnlyUsers-${data.id}`}
          label={t('sortAdminOnlyUsers')}
          value={data.admin_only_users_count}
        />
      </View>
      <ShippedBar data={data} />
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginTop: 10
        }}
      >
        <Text
          testID={`phoneActivity-${data.id}`}
          numberOfLines={1}
          style={{
            flex: 1,
            fontFamily: fontFamily.font500,
            fontSize: 12,
            color: textMutedColor
          }}
        >
          {latest ? formatActivityTime(latest.time) : t('noActivityYet')}
        </Text>
        {selectMode ? null : (
          <Text style={{fontSize: 20, color: textMutedColor}}>{'›'}</Text>
        )}
      </View>
    </View>
  );
};

export default TenantPhoneCard;
